import { createSelector } from "reselect"
import { getForcedCurrencyScale, makeCurrencyFormatter } from "helpers/formatters/numbers"

export type ValueFormatter = (value: unknown) => string

interface FallbackFormatterProps {
  formatter?: ValueFormatter
}

export function getCurrencyScaleForValues(values: readonly unknown[]): string {
  const maxMagnitude = values.reduce<number>(
    (largestMagnitude, value) => typeof value === "number" && Number.isFinite(value)
      ? Math.max(largestMagnitude, Math.abs(value))
      : largestMagnitude,
    0,
  )
  return getForcedCurrencyScale(maxMagnitude)
}

export function makeScaledValueFormatter(currencyScale: string) {
  return makeCurrencyFormatter(undefined, { forcedScale: currencyScale, valueOnly: true })
}

export function makeScaledFormatterSelector<State, Props extends FallbackFormatterProps>(
  getCurrencyScale: (state: State, props: Props) => string | null,
) {
  const getFallbackFormatter = (_: State, props: Props) => props.formatter
  return createSelector(
    [getCurrencyScale, getFallbackFormatter],
    (currencyScale, fallbackFormatter): ValueFormatter | undefined => currencyScale
      ? makeScaledValueFormatter(currencyScale)
      : fallbackFormatter,
  )
}

export interface CurrencyScaleAndFormatter {
  currencyScale: string
  formatter: ValueFormatter
}

export function getCurrencyScaleAndFormatterForValues(values: readonly unknown[]): CurrencyScaleAndFormatter {
  const currencyScale = getCurrencyScaleForValues(values)
  return { currencyScale, formatter: makeScaledValueFormatter(currencyScale) }
}
